import { TApp } from '../types';

type TFeatureToggleConfig = {
  [K in TApp]: {
    [feature: string]: {
      key: string;
    };
  };
};

const FeatureToggleConfig: TFeatureToggleConfig = {
  'admin-portal': {
    isUsingNewCustomerLookupApi: {
      key: 'NEXT_PUBLIC_FEATURE_TOGGLE_USE_NEW_CUSTOMER_LOOKUP_API',
    },
    isTrackingReferencesReportEnabled: {
      key: 'NEXT_PUBLIC_FEATURE_TOGGLE_TRACKING_REFERENCES_REPORT',
    },
  },
  'client-portal': {
    isInertMagicLinkEnabled: {
      key: 'NEXT_PUBLIC_FEATURE_TOGGLE_INERT_MAGIC_LINK',
    },
    // Zone B (client-portal-integrations) borrows this set: its .env sets
    // NEXT_PUBLIC_APP=client-portal, because TApp has no Zone B member yet.
  },
  'customer-portal': {
    isSyncUploadFaceCheckVideo: {
      key: 'NEXT_PUBLIC_FEATURE_TOGGLE_SYNC_UPLOAD_FACE_CHECK_VIDEO',
    },
    isVasAutocompleteEnabled: {
      key: 'NEXT_PUBLIC_FEATURE_TOGGLE_VAS_AUTOCOMPLETE',
    },
    isCustomerOnboardingSignUpEnabled: {
      key: 'NEXT_PUBLIC_FEATURE_TOGGLE_CUSTOMER_ONBOARDING_SIGN_UP',
    },
    isInertMagicLinkEnabled: {
      key: 'NEXT_PUBLIC_FEATURE_TOGGLE_INERT_MAGIC_LINK',
    },
    isUsingCustomerIdentityV2: {
      key: 'NEXT_PUBLIC_FEATURE_TOGGLE_USE_CUSTOMER_IDENTITY_V2',
    },
    isEntitySelectionFirstEnabled: {
      key: 'NEXT_PUBLIC_FEATURE_TOGGLE_ENTITY_SELECTION_FIRST',
    },
  },
} as const;

// Generate TFeatureToggle from FeatureToggleConfig
export type TFeatureToggle = {
  [K in keyof (typeof FeatureToggleConfig)[TApp]]: boolean;
};

// Due to known limitations of Next.js, we cannot use dynamic keys to access environment variables.
// Which means that `process.env[dynamicKey]` will not work as expected in the client-side code.
// Therefore, we statically read all relevant env configs at build time
const featureToggleEnv: Record<string, string | undefined> = {
  NEXT_PUBLIC_FEATURE_TOGGLE_USE_NEW_CUSTOMER_LOOKUP_API:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_USE_NEW_CUSTOMER_LOOKUP_API,
  NEXT_PUBLIC_FEATURE_TOGGLE_SYNC_UPLOAD_FACE_CHECK_VIDEO:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_SYNC_UPLOAD_FACE_CHECK_VIDEO,
  NEXT_PUBLIC_FEATURE_TOGGLE_VAS_AUTOCOMPLETE:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_VAS_AUTOCOMPLETE,
  NEXT_PUBLIC_FEATURE_TOGGLE_CUSTOMER_ONBOARDING_SIGN_UP:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_CUSTOMER_ONBOARDING_SIGN_UP,
  NEXT_PUBLIC_FEATURE_TOGGLE_INERT_MAGIC_LINK:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_INERT_MAGIC_LINK,
  NEXT_PUBLIC_FEATURE_TOGGLE_USE_CUSTOMER_IDENTITY_V2:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_USE_CUSTOMER_IDENTITY_V2,
  NEXT_PUBLIC_FEATURE_TOGGLE_ENTITY_SELECTION_FIRST:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_ENTITY_SELECTION_FIRST,
  NEXT_PUBLIC_FEATURE_TOGGLE_TRACKING_REFERENCES_REPORT:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_TRACKING_REFERENCES_REPORT,
};

function resolveToggleValue(key: string): boolean {
  return featureToggleEnv[key] === 'on';
}

export function useFeatureToggle() {
  const app = process.env.NEXT_PUBLIC_APP as TApp;
  if (!app) {
    return {} as TFeatureToggle;
  }

  const config = FeatureToggleConfig[app];
  if (!config) {
    return {} as TFeatureToggle;
  }

  const toggles = getFeatureToggleConfigWithResolvedValue(app);

  return Object.entries(toggles).reduce((acc, [key, config]) => {
    acc[key] = config.resolvedValue;
    return acc;
  }, {} as TFeatureToggle);
}

export type TFeatureToggleConfigDetails = {
  key: string;
  resolvedValue: boolean;
};

export type TFeatureToggleConfigWithResolvedValue = {
  [feature: string]: TFeatureToggleConfigDetails;
};

/**
 * Returns the feature toggle config for a given app,
 * with each feature toggle including a `configedValue` property
 * representing the configured env value (from env) and a `resolvedValue` property
 * representing the resolved value (from env or default).
 */
export function getFeatureToggleConfigWithResolvedValue(
  app: TApp,
): TFeatureToggleConfigWithResolvedValue {
  const config = FeatureToggleConfig[app];
  if (!config) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(config).map(([feature, { key }]) => {
      return [
        feature,
        {
          key,
          resolvedValue: resolveToggleValue(key),
        },
      ];
    }),
  );
}
